// #region Imports
import { createContext, useCallback, useContext, useReducer } from 'react';
import { authInstance, instance } from '../../API/GoMoto';
import { useAdditionalDealers, useKioskState, useSwitchDealer } from '../Kiosk/kiosk';
import { useLocalizationDispatch, useLocalization } from '../Localization/localization';
import { Locales, LocalizationDispatchTypes } from '../Localization/localization.types';
import {
    AdminContextState,
    AdminDispatch,
    AdminDispatchContextType,
    AdminDispatchHook,
    AdminDispatchTypes,
    AdminLoginHook,
    AdminLoginResult,
    AdminLogoutHook,
    AdminProviderProps,
    AdminQrLoginHook,
    AdminReducerFunction,
    AdminState,
    AdminStateContextType,
    AdminStateHook,
    APIError,
    Language,
    RequestMFACodeHook,
    RequestMFACodeResult,
    ResolveLocaleFunction,
} from './admin.types';
import { GoMotoComponent } from '../../API/GoMoto.types';
import { AxiosError } from 'axios';
import { Dealer } from '../Kiosk/kiosk.types';
// #endregion

// #region Setup
/**
 * Defines the initial state object of the admin context with all properties set to undefined.
 */
const initialState: AdminContextState = {
    LoginSessionID: undefined,
    userID: undefined,
    PhoneNumberMFADisplay: undefined,
    EmailAddressMFADisplay: undefined,
    PIN: undefined,
    LastLockerChanged: undefined,
    canAccessDealer: undefined,
};

/**
 * Sets up the admin state context and defaults it to undefined so that if any admin context hook is used outside of the
 * admin state context, we can throw an error.
 */
const AdminStateContext: AdminStateContextType = createContext<AdminState>(undefined);

/**
 * Sets up the admin dispatch context and defaults it to undefined so that if any admin hook that uses the dispatch context is
 * used outside of the admin dispatch context, we can throw an error.
 */
const AdminDispatchContext: AdminDispatchContextType = createContext<AdminDispatch>(undefined);
// #endregion

// #region Reducer
/**
 * Handles a single action dispatch to the admin context to update the internal state
 * of the context.
 *
 * @param state - {@link AdminReducerFunction state} description.
 * @param action - {@link AdminReducerFunction action} description.
 */
export const adminReducer: AdminReducerFunction = (state, action) => {
    switch (action.type) {
        case AdminDispatchTypes.SetLoginSession:
            return {
                ...state,
                LoginSessionID: action.payload.sessionId,
                userID: action.payload.userId,
                PhoneNumberMFADisplay: undefined,
                EmailAddressMFADisplay: undefined,
                PIN: undefined,
                canAccessDealer: action.payload.canAccessDealer,
            };
        case AdminDispatchTypes.ClearLoginSession:
            return {
                ...state,
                LoginSessionID: undefined,
                userID: undefined,
                PhoneNumberMFADisplay: undefined,
                EmailAddressMFADisplay: undefined,
                PIN: undefined,
            };
        case AdminDispatchTypes.SetMFAData:
            return {
                ...state,
                LoginSessionID: undefined,
                userID: undefined,
                PhoneNumberMFADisplay: action.payload.PhoneNumberMFADisplay,
                EmailAddressMFADisplay: action.payload.EmailAddressMfaDisplay,
                PIN: action.payload.PIN,
            };
        case AdminDispatchTypes.SetLockerChanged:
            return {
                ...state,
                LastLockerChanged: action.payload,
            };
        default:
            return state;
    }
};
// #endregion

// #region Provider
/**
 * Provides the admin state and a dispatch to update the state to all components that
 * are rendered as a child node of this component.
 *
 * @param children - {@link AdminProviderProps children} description.
 */
export const AdminProvider: GoMotoComponent<AdminProviderProps> = ({
    children,
}) => {
    const [state, dispatch] = useReducer<AdminReducerFunction>(adminReducer, initialState);

    return (
        <AdminStateContext.Provider value={state}>
            <AdminDispatchContext.Provider value={dispatch}>
                {children}
            </AdminDispatchContext.Provider>
        </AdminStateContext.Provider>
    );
};
// #endregion

// #region useAdminState
/**
 * Admin context hook that retrieves the state from the context, or throws
 * an error if not used in a component that is a child of the Admin context.
 */
export const useAdminState: AdminStateHook = () => {
    const context = useContext(AdminStateContext);

    if (context === undefined) {
        throw new Error('useAdminState must be used within an AdminProvider');
    }

    return context;
};
// #endregion

// #region useAdminDispatch
/**
 * Admin context hook that returns a dispatch function which can dispatch actions to
 * update the state of the Admin context, or throws an error if not used in a
 * component that is a child of the Admin context.
 */
export const useAdminDispatch: AdminDispatchHook = () => {
    const context = useContext(AdminDispatchContext);

    if (context === undefined) {
        throw new Error('useAdminDispatch must be used within an AdminProvider');
    }

    return context;
};
// #endregion

// #region useResolveLocale
/**
 * Converts a number representing a language into the correct Locale equivalent.
 *
 * @param language - {@link ResolveLocaleReturn language} description.
 */
const resolveLocale: ResolveLocaleFunction = (language) => {
    switch (language) {
        case Language.EnUs:
            return Locales.en;
        case Language.EsUs:
            return Locales.es;
        case Language.FrCa:
            return Locales.fr;
        default:
            return undefined;
    }
};
// #endregion

// #region useAdminLogin
/**
 * Returns an asynchronous function that begins a `ValidateAdminLogin`API call to validate the current
 * PIN. If there is an error, only error data is returned by the request and we return the result with only the error data.
 * Otherwise the function will return the resulting data from the reqeust and dispatch to the admin and localization providers.
 */
export const useAdminLogin: AdminLoginHook = () => {
    const dispatch = useAdminDispatch();
    const localeDispatch = useLocalizationDispatch();
    const Localization = useLocalization();
    const {
        dealer: { primaryLanguage },
    } = useKioskState();

    return useCallback(async ({ kioskKey, dealerKey, pin, code }) => {
        const result: AdminLoginResult = {
            error: false,
            message: undefined,
            sessionId: undefined,
            userId: undefined,
            phoneNumberMfaDisplay: undefined,
            emailAddressMfaDisplay: undefined,
            language: undefined,
            dealerKey: undefined,
            canAccessDealer: undefined,
        };

        await instance
            .get<AdminLoginResult>('ValidateAdminLogin', { params: { kioskKey,
                dealerKey,
                pin,
                code } })
            .then((response) => {
                if (response) {
                    const { data } = response;
                    result.language = data.language;
                    result.canAccessDealer = data.canAccessDealer;

                    /**
                     * Even though data will be defined, legacy pins will return data as an empty string.
                     * So we check if it is falsy here to avoid updating result with MFA data that does
                     * not exist.
                     */
                    if (data) {
                        if (!data.sessionId) {
                            result.emailAddressMfaDisplay = data.emailAddressMfaDisplay;
                            result.phoneNumberMfaDisplay = data.phoneNumberMfaDisplay;
                            result.sessionId = undefined;
                        }
                        else {
                            result.sessionId = data.sessionId;
                            result.userId = data.userId;
                        }
                    }
                    else if (!result.error) {
                        result.sessionId = '1';
                    }
                }

            })
            .catch((error: AxiosError<APIError>) => {
                result.error = true;
                result.message = error?.response?.data?.message;
            });

        if (!result.error) {
            if (!result.sessionId) {
                dispatch({
                    type: AdminDispatchTypes.SetMFAData,
                    payload: {
                        PhoneNumberMFADisplay: result.phoneNumberMfaDisplay,
                        EmailAddressMfaDisplay: result.emailAddressMfaDisplay,
                        PIN: pin,
                    },
                });
            }
            else {
                const language: Locales = resolveLocale(result.language) ?? primaryLanguage;

                dispatch({
                    type: AdminDispatchTypes.SetLoginSession,
                    payload: {
                        sessionId: result.sessionId ?? '1',
                        userId: result.userId,
                        canAccessDealer: result.canAccessDealer,
                    },
                });

                if (!Localization.getAvailableLanguages()
                    .includes(language)) {
                    localeDispatch(
                        {
                            type: LocalizationDispatchTypes.EnableLocale,
                            payload: language,
                        },
                        {
                            type: LocalizationDispatchTypes.SetLocale,
                            payload: language,
                        },
                    );
                }
                else {
                    localeDispatch({
                        type: LocalizationDispatchTypes.SetLocale,
                        payload: language,
                    });
                }
            }
        }

        return result;
    }, [Localization, dispatch, localeDispatch, primaryLanguage]);
};
// #endregion

// #region useAdminQRLogin
/**
 * Returns an asynchronous function that begins a `ValidateKioskMFAQRCodeLogin`API call to validate the current MFA code.
 * If there is an error, only error data is returned by the request and we return the result with only the error data.
 * Otherwise the function will return the resulting data from the request and dispatch to the admin and localization providers.
 * If a dealer key is returned, the function will also switch dealers to the dealer returned.
 */
export const useAdminQrLogin: AdminQrLoginHook = () => {
    const adminDispatch = useAdminDispatch();
    const switchDealer = useSwitchDealer();
    const additionalDealers = useAdditionalDealers();
    const localeDispatch = useLocalizationDispatch();
    const {
        dealer: { primaryLanguage, key },
    } = useKioskState();

    return useCallback(async ({ kioskKey, qrCode }) => {
        const result: AdminLoginResult = {
            error: false,
            message: undefined,
            sessionId: undefined,
            userId: undefined,
            phoneNumberMfaDisplay: undefined,
            emailAddressMfaDisplay: undefined,
            language: undefined,
            dealerKey: undefined,
        };

        await instance
            .get<AdminLoginResult>('ValidateKioskMFAQRCodeLogin', { params: { kioskKey,
                qrCode } })
            .then((response) => {
                /**
                 * Even though data will be defined, we are checking if data is falsy
                 * since legacy pins can return data as an empty string.
                 *
                 * It is also possible to return no data with a successful response due to how the
                 * end point is set up.
                 */
                if (response && response.data) {
                    const { data } = response;

                    result.sessionId = data.sessionId;
                    result.userId = data.userId;
                    result.dealerKey = data.dealerKey;
                    result.language = data.language;
                }
            })
            .catch((error: AxiosError<APIError>) => {
                result.error = true;
                result.message = error?.response?.data?.message;
            });

        if (!result.error) {
            const language: Locales = resolveLocale(result.language) ?? primaryLanguage;

            adminDispatch({
                type: AdminDispatchTypes.SetLoginSession,
                payload: {
                    sessionId: result.sessionId ?? '1',
                    userId: result.userId,
                    canAccessDealer: result.canAccessDealer,
                },
            });

            localeDispatch(
                {
                    type: LocalizationDispatchTypes.EnableLocale,
                    payload: language,
                },
                {
                    type: LocalizationDispatchTypes.SetLocale,
                    payload: language,
                },
            );

            if (result.dealerKey) {
                const newDealer = additionalDealers.find((dealer: Dealer) => dealer.key === result.dealerKey);
                newDealer && newDealer.key !== key && switchDealer(newDealer);
            }
        }

        return result;
    }, [additionalDealers, adminDispatch, key, localeDispatch, primaryLanguage, switchDealer]);
};
// #endregion

// #region useRequestMFACode
/**
 * Returns an asynchronous function that begins a `RequestMFACode` API call. No data will
 * be returned by the call, so we only listen for an error and return the result containing whether
 * there was an error and what the error message was.
 */
export const useRequestMFACode: RequestMFACodeHook = () => {
    return useCallback(async ({ kioskKey, dealerKey, pin, deliveryMethod }) => {
        const result: RequestMFACodeResult = {
            error: false,
            message: undefined,
        };

        await instance
            .get<void>('RequestMFACode', { params: { kioskKey,
                dealerKey,
                pin,
                deliveryMethod } })
            .catch((error: AxiosError<APIError>) => {
                result.error = true;
                result.message = error?.response?.data?.message;
            });

        return result;
    }, []);
};
// #endregion

// #region useAdminLogout
/**
 * Returns an asynchronous function that dispatches a `ClearLoginSession` action to the admin context,
 * and then begins a `/Login/Logout` API call if the current LoginSessionID is not equal to `1`.
 */
export const useAdminLogout: AdminLogoutHook = () => {
    const { LoginSessionID } = useAdminState();
    const dispatch = useAdminDispatch();

    return useCallback(async (autoLogout: boolean) => {
        if (LoginSessionID) {
            dispatch({ type: AdminDispatchTypes.ClearLoginSession });

            if (LoginSessionID !== '1') {
                await authInstance.put<void>(`/Login/Logout?auto=${autoLogout}`, JSON.stringify(LoginSessionID));
            }
        }
    }, [LoginSessionID, dispatch]);
};
// #endregion
